/**
 * Reading the player pool payload from `/api/players/`.
 *
 * Every record carries a `fantasy_data` block keyed by scoring type, each with
 * its own ADP and expert-consensus (ECR) ranks. Pulling those apart is fiddly
 * enough -- and diagnostic enough, when ADP looks wrong -- to keep out of the
 * component and under test.
 */

export const SCORING_TYPES = [
  { value: 'ppr', label: 'PPR' },
  { value: 'half_ppr', label: 'Half PPR' },
  { value: 'non_ppr', label: 'Standard' },
  { value: 'superflex_ppr', label: 'Superflex PPR' },
  { value: 'superflex_half_ppr', label: 'Superflex Half' },
  { value: 'superflex_non_ppr', label: 'Superflex Standard' },
];

export const POSITIONS = ['QB', 'RB', 'WR', 'TE', 'K', 'DST'];

const asNumber = (value) => (typeof value === 'number' && !Number.isNaN(value) ? value : null);

/** Unwrap either a paginated envelope or a bare array. */
export const readResults = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

/**
 * Flatten one player against one scoring board.
 *
 * `adpRank` and `ecrRank` are kept apart on purpose. The keeper table falls back
 * to the ECR rank when a player has no ADP, which silently prices him off expert
 * consensus under an "ADP" heading -- so here the two are separate columns and
 * `adpSource` says which feed the ADP actually came from.
 */
export const toRow = (player, scoringType) => {
  const block = (player?.fantasy_data || {})[scoringType] || {};
  return {
    id: player?.id,
    name: player?.name || '—',
    position: player?.position || null,
    team: player?.team || null,
    byeWeek: asNumber(player?.bye_week),
    adpRank: asNumber(block.adp_rank),
    adpRaw: asNumber(block.adp_raw),
    adpSource: block.adp_source || null,
    adpStdev: asNumber(block.adp_stdev),
    ecrRank: asNumber(block.ovr_rank),
    ecrTier: asNumber(block.ovr_tier),
    positionRank: asNumber(block.pos_rank),
  };
};

/** The draft round an overall rank falls in, for a league of `teamCount`. */
export const toRound = (rank, teamCount) => {
  if (rank === null || !teamCount) return null;
  return Math.ceil(rank / teamCount);
};

/**
 * What the pool actually contains for one board: how many players carry ADP and
 * which feed supplied it. This is the line to read first when the numbers look
 * like the wrong season -- a source or a count that surprises you is the answer.
 */
export const summarize = (rows) => {
  const withAdp = rows.filter((r) => r.adpRank !== null);
  const sources = [...new Set(withAdp.map((r) => r.adpSource).filter(Boolean))].sort();
  return { total: rows.length, withAdp: withAdp.length, sources };
};

const compare = (a, b, key) => {
  const av = a[key];
  const bv = b[key];
  // Missing values sort last in both directions -- an unranked player is not
  // "rank zero", and letting nulls float to the top buries the real board.
  if (av === null || av === undefined) return bv === null || bv === undefined ? 0 : 1;
  if (bv === null || bv === undefined) return -1;
  if (typeof av === 'string') return av.localeCompare(bv);
  return av - bv;
};

export const sortRows = (rows, key, direction) => {
  const sorted = [...rows].sort((a, b) => compare(a, b, key));
  if (direction === 'desc') {
    // Reverse only the entries that have a value, so nulls stay at the bottom.
    const present = sorted.filter((r) => r[key] !== null && r[key] !== undefined).reverse();
    const missing = sorted.filter((r) => r[key] === null || r[key] === undefined);
    return [...present, ...missing];
  }
  return sorted;
};

export const filterRows = (rows, { search = '', positions = [] } = {}) => {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (positions.length && !positions.includes(row.position)) return false;
    if (!needle) return true;
    return (
      row.name.toLowerCase().includes(needle) ||
      (row.team || '').toLowerCase().includes(needle)
    );
  });
};
